import React, { useEffect, useState } from 'react';
import { FaTimes, FaTools } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import { toast } from 'react-toastify';
import { toMask } from '../../services/currency';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import CustomModal from '../CustomModal/CustomModal';
import {
  EstimateService,
  EstimateViewModel,
} from '../../models/estimate/EstimateViewModel';
import CustomButton from '../CustomButton/CustomButton';
import EstimateStatus from '../../enums/EstimateStatus';

interface EstimateServicesProps {
  estimate: EstimateViewModel;
  items: EstimateService[];
  refreshServices: () => void;
  removeService: (estimateService: EstimateService) => void;
}

type ServiceModel = {
  id: number;
  name: string;
  price: number;
  isGrouped: boolean;
};

interface EstimateServiceFormFields {
  service: number;
  quantity: number;
  isGrouped: boolean;
  description?: string;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function EstimateServices({
  estimate,
  items,
  refreshServices,
  removeService,
}: EstimateServicesProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<EstimateServiceFormFields>({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [services, setServices] = useState<ServiceModel[]>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    user: { organization },
  } = useAuth();

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  async function getServices() {
    if (organization) {
      setIsLoading(true);
      setIsOpenModal(true);
      const response = await api.get(
        `organizations/${organization.id}/estimates/${estimate.id}/services/available`
      );
      // TODO: Remover os que já estão adicionados
      setServices(response.data.data);
      setIsLoading(false);
    }
  }

  const onSubmit = async (data: EstimateServiceFormFields) => {
    try {
      await api.post(
        `organizations/${organization.id}/estimates/${estimate.id}/services`,
        data
      );

      setServices([]);
      setIsOpenModal(false);
      refreshServices();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  return (
    <>
      <span className='h2'>Serviços</span>
      <div className='table-responsive'>
        <table className='table'>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrição</th>
              <th>Quantidade</th>
              <th className='text-end'>Valor</th>
              <th className='text-end'>Total</th>
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.description ?? 'Sem descrição'}</td>
                  <td>{item.quantity}</td>
                  <td className='text-end'>{toMask(item.price)}</td>
                  <td className='text-end'>
                    {toMask(item.quantity * item.price)}
                  </td>
                  <td>
                    {estimate.status !== EstimateStatus.Completed &&
                      estimate.status !== EstimateStatus.Canceled && (
                        <button
                          className='btn btn-danger btn-sm'
                          type='button'
                          onClick={() => removeService(item)}
                        >
                          <FaTimes />
                        </button>
                      )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>Nenhum registro</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='text-end'>
        {estimate.status !== EstimateStatus.Completed &&
          estimate.status !== EstimateStatus.Canceled && (
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              onClick={() => getServices()}
            >
              Adicionar serviço <FaTools />
            </button>
          )}

        <CustomModal
          modalTitle='Adicionar serviço'
          isOpen={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          fullscreenOnMobile={false}
        >
          <form
            style={{
              paddingTop: '1rem',
              paddingBottom: '1rem',
            }}
          >
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className='form-label'>Selecione o serviço</label>
              <select
                required
                className='form-select'
                aria-label='Selecione o serviço'
                {...register('service', { required: true })}
              >
                <option value=''>Selecione o serviço</option>
                {services.map((service) => (
                  <option value={service.id} key={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              {errors.service && (
                <div className='form-text text-danger'>
                  {errors.service.message}
                </div>
              )}
            </div>
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor='inputLabel' className='form-label'>
                Quantidade
              </label>
              <input
                {...register('quantity', { required: true })}
                className='form-control'
                placeholder='Quantidade'
                type='number'
                min='1'
                step='1'
                required
              />
              {errors.quantity && (
                <div className='form-text text-danger'>
                  {errors.quantity.message}
                </div>
              )}
            </div>
            <div className='col-sm-3'>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  role='switch'
                  id='isGrouped'
                  {...register('isGrouped')}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className='form-check-label' htmlFor='isGrouped'>
                  Agrupado
                </label>
              </div>
            </div>
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor='inputLabel' className='form-label'>
                Descrição
              </label>
              <textarea
                {...register('description')}
                className='form-control'
                placeholder='Descrição'
              />
              {errors.description && (
                <div className='form-text text-danger'>
                  {errors.description.message}
                </div>
              )}
            </div>
            <div className='text-end mt-3'>
              <CustomButton
                type='button'
                className='btn btn-primary'
                handleClick={handleSubmit(onSubmit)}
              >
                Adicionar
              </CustomButton>
            </div>
          </form>
        </CustomModal>
      </div>
    </>
  );
}
